import moment from 'moment-timezone';

import type { Booking, Location, Study } from '../../types';
import type { LegacyStudyScanUpdatesPayload } from '../../store/studies/actions';

const CLINIC_DATE = 'YYYY-MM-DD';

/** `utcStart` / slot selection use Unix seconds (same as calendar APIs). */
function clinicLocalYmd(utcSeconds: number, timeZone: string): string {
  return moment.unix(utcSeconds).tz(timeZone).format(CLINIC_DATE);
}

function previousClinicYmd(
  timeZone: string,
  currentBooking: Pick<Booking, 'utcStart' | 'skuIDs'> | undefined,
): string | null {
  const raw = currentBooking?.utcStart;
  if (raw == null || String(raw) === '') {
    return null;
  }
  return clinicLocalYmd(Number(raw), timeZone);
}

type BuildPayloadArgs = {
  study: Pick<Study, 'status' | 'sfOpportunityID' | 'orderId'>;
  location: Pick<Location, 'timezone' | 'type'>;
  /** Unix seconds for the new slot (same as `Booking.utcStart` / StudyContext). */
  appointmentTime: number | null;
  currentBooking: Pick<Booking, 'utcStart' | 'skuIDs'> | undefined;
  skuId: string;
};

/**
 * Resolves the Rosetta legacy scan-updates body for an assisted save, or `null` when the
 * endpoint should not be called.
 */
export function buildLegacyStudyScanUpdatesPayload({
  study,
  location,
  appointmentTime,
  currentBooking,
  skuId,
}: BuildPayloadArgs): LegacyStudyScanUpdatesPayload | null {
  if (appointmentTime == null) {
    return null;
  }

  const opportunityId = study.sfOpportunityID?.trim();
  // eslint-disable-next-line camelcase
  const orderId = study.poetryMetadata?.order_id?.trim();
  if (!opportunityId && !orderId) {
    return null;
  }
  if (location.type === 'whitelabel' && !opportunityId) {
    return null;
  }

  const timeZone = location.timezone;
  const newYmd = clinicLocalYmd(Number(appointmentTime), timeZone);
  const oldYmd = previousClinicYmd(timeZone, currentBooking);

  const clinicDayChanged = !study.status.booked || oldYmd == null || newYmd !== oldYmd;
  const previousSku = currentBooking?.skuIDs?.[0];
  const skuChanged = previousSku != null && skuId !== previousSku;

  if (!clinicDayChanged && !skuChanged) {
    return null;
  }

  const shouldSendSku = !study.status.booked || skuChanged;
  const sku = skuId.trim();
  return {
    closeDate: newYmd,
    ...(opportunityId ? { opportunityId } : {}),
    ...(orderId ? { orderId } : {}),
    ...(shouldSendSku && sku !== '' ? { sku } : {}),
  };
}
